import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchEntryPage, FetchEntryPageResponse } from '../../api/entryPage/fetchEntryPage';
import { EntryPageType } from '../../../models/EntryPage';
import { ProductMetaData } from '../../../models/Product';
import { isLoanFormat, LoanFormat } from '../../../models/LoanFormat';
import { AgeCategory } from '../../../models/AgeCategory';
import { GlobalReactQueryOptions } from '../../config';
import {
    BannerSlider,
    BucketEntry,
    FilterSettingLegacy,
    LCProductGroupOut,
    ProductFilter,
    QuicklinkOut
} from '../../store/api';
import { EMPTY_PRODUCT } from '../../api/ProductOutMappings';
import { UseQueryResult } from '../../api/query-types';
import mapValues from 'lodash/mapValues';

export type UseEntryPageQueryData = {
    readonly topBanner?: BannerSlider;
    readonly entryPageItems: ReadonlyArray<LCProductGroupOut | BannerSlider>;
    readonly sidebar: {
        readonly available: boolean;
        readonly quicklinks: ReadonlyArray<QuicklinkOut>;
        readonly titleOfMonth: ProductMetaData;
        readonly topEaudiobooks: ReadonlyArray<ProductMetaData>;
        readonly topEbooks: ReadonlyArray<ProductMetaData>;
        readonly topEpress: ReadonlyArray<ProductMetaData>;
        readonly languages: ReadonlyArray<BucketEntry>;
        readonly topLanguages: ReadonlyArray<BucketEntry>;
    };
};

type UseEntryPageQuery = UseQueryResult<'data', UseEntryPageQueryData>;

type UseEntryPageOptions = {
    readonly loanFormat?: LoanFormat;
    readonly ageCategory?: AgeCategory;
    readonly shopLanguage?: string;
};

const emptyEntryPage = {
    banners: [],
    entryPageItems: [],
    sidebar: {
        available: false,
        quicklinks: [],
        titleOfMonth: EMPTY_PRODUCT,
        topEaudiobooks: [],
        topEbooks: [],
        topEpress: [],
        languages: [],
        topLanguages: []
    }
};

export const useEntryPageQuery = (
    entryPage: EntryPageType,
    { loanFormat, ageCategory, shopLanguage }: UseEntryPageOptions = {}
): UseEntryPageQuery => {
    const params = {
        loanFormat: isLoanFormat(loanFormat) ? loanFormat : undefined,
        ageCategory,
        shopLanguage
    };
    const { data, ...result } = useQuery(['entryPage', entryPage, params], () => fetchEntryPage(entryPage, params), {
        ...GlobalReactQueryOptions
    });

    return {
        ...result,
        data: useMemo(() => mapEntryPageApiToQueryResponse(data), [data])
    };
};

const mapEntryPageApiToQueryResponse = (resp?: FetchEntryPageResponse): UseEntryPageQueryData => {
    if (!resp) {
        return emptyEntryPage;
    }

    const mappedEntryPageItems = resp.entryPageItems.map(entryPageItem => {
        if (!('productSpec' in entryPageItem && entryPageItem.productSpec && entryPageItem.productSpec.filters)) {
            return entryPageItem;
        }

        return {
            ...entryPageItem,
            productSpec: {
                ...entryPageItem.productSpec,
                filters: mapProductSpecFilters(entryPageItem.productSpec.filters)
            }
        };
    });

    const topBanner =
        mappedEntryPageItems.length > 0 && mappedEntryPageItems[0].type === 'banner'
            ? mappedEntryPageItems[0]
            : undefined;
    const entryPageItems = !topBanner ? mappedEntryPageItems : mappedEntryPageItems.slice(1);

    return {
        topBanner,
        entryPageItems,
        sidebar: resp.sidebar
    };
};

const mapProductSpecFilters = (filters: { [key: string]: FilterSettingLegacy } | undefined): ProductFilter => {
    return mapValues(filters, (filterSetting: FilterSettingLegacy) => ({
        include: filterSetting.include ?? filterSetting.includeFilterValues ?? [],
        exclude: filterSetting.exclude ?? filterSetting.excludeFilterValues ?? []
    }));
};
